import { motion } from "framer-motion";

const COLORS = ["#0E8F9E", "#29B3C2", "#F5C542", "#123C52", "#2AA9B8", "#F6CA4A"];

/* ============================================================
   1. WIREFRAME GLOBE with colorful animated delivery arcs + cities
   ============================================================ */
export const GlobeGraphic = ({ className = "" }) => {
  const R = 190, C = 200;
  const meridians = [190, 150, 104, 52];
  const latitudes = [150, 104, 52];
  const cities = [
    { x: 150, y: 120 }, { x: 210, y: 150 }, { x: 245, y: 172 },
    { x: 300, y: 210 }, { x: 250, y: 235 }, { x: 92, y: 190 },
  ];
  const arcs = [[5, 0], [0, 1], [1, 2], [2, 4], [4, 3], [1, 3]];
  const arcPath = (a, b) => {
    const p1 = cities[a], p2 = cities[b];
    const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2;
    const dx = mx - C, dy = my - C;
    const cx = mx + dx * 0.4, cy = my + dy * 0.4 - 30;
    return `M ${p1.x} ${p1.y} Q ${cx} ${cy} ${p2.x} ${p2.y}`;
  };

  return (
    <div aria-hidden className={`relative ${className}`}>
      <svg viewBox="0 0 400 400" className="h-full w-full overflow-visible">
        <defs>
          <radialGradient id="globe-fill" cx="42%" cy="38%" r="70%">
            <stop offset="0%" stopColor="#0E8F9E" stopOpacity="0.3" />
            <stop offset="45%" stopColor="#29B3C2" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#F5C542" stopOpacity="0" />
          </radialGradient>
          {arcs.map((_, i) => (
            <linearGradient key={i} id={`arc-${i}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={COLORS[i % COLORS.length]} />
              <stop offset="100%" stopColor={COLORS[(i + 2) % COLORS.length]} />
            </linearGradient>
          ))}
        </defs>

        <circle cx={C} cy={C} r={R} fill="url(#globe-fill)" />

        <motion.g
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ duration: 1 }}
          stroke="currentColor" strokeOpacity="0.16" fill="none" strokeWidth="1"
        >
          <circle cx={C} cy={C} r={R} strokeOpacity="0.3" />
          <line x1={C} y1={C - R} x2={C} y2={C + R} />
          <line x1={C - R} y1={C} x2={C + R} y2={C} />
          {meridians.map((rx, i) => <ellipse key={`m${i}`} cx={C} cy={C} rx={rx} ry={R} />)}
          {latitudes.map((ry, i) => <ellipse key={`l${i}`} cx={C} cy={C} rx={R} ry={ry} />)}
        </motion.g>

        {arcs.map(([a, b], i) => (
          <motion.path
            key={i}
            d={arcPath(a, b)}
            stroke={`url(#arc-${i})`}
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
          />
        ))}

        {cities.map((c, i) => (
          <g key={i}>
            <motion.circle
              cx={c.x} cy={c.y} r="4" fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="1.5"
              initial={{ scale: 0, opacity: 0.8 }}
              animate={{ scale: [0.5, 3], opacity: [0.8, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.4 }}
              style={{ transformOrigin: `${c.x}px ${c.y}px` }}
            />
            <circle cx={c.x} cy={c.y} r="4" fill={COLORS[i % COLORS.length]} />
          </g>
        ))}
      </svg>
    </div>
  );
};

/* ============================================================
   2. NEURAL CORE — colorful pulsing centre, orbiting rings + satellites
   ============================================================ */
export const NeuralCore = ({ className = "" }) => {
  const rings = [
    { size: 46, dur: 22, dir: 1, color: "#29B3C2" },
    { size: 68, dur: 30, dir: -1, color: "#0E8F9E" },
    { size: 92, dur: 40, dir: 1, color: "#F5C542" },
  ];
  return (
    <div aria-hidden className={`relative aspect-square w-full ${className}`}>
      <div className="absolute inset-0 dot-grid opacity-[0.12]" />
      <motion.div
        className="absolute inset-0 m-auto rounded-full blur-2xl"
        style={{ width: "48%", height: "48%", background: "conic-gradient(#123C52,#0E8F9E,#F5C542,#29B3C2,#123C52)" }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4], rotate: 360 }}
        transition={{ scale: { duration: 4.5, repeat: Infinity }, opacity: { duration: 4.5, repeat: Infinity }, rotate: { duration: 24, repeat: Infinity, ease: "linear" } }}
      />
      {rings.map((r, i) => (
        <motion.div
          key={i}
          className="absolute inset-0 m-auto rounded-full"
          style={{ width: `${r.size}%`, height: `${r.size}%`, border: `1.5px solid ${r.color}66` }}
          animate={{ rotate: 360 * r.dir }}
          transition={{ duration: r.dur, repeat: Infinity, ease: "linear" }}
        >
          <span
            className="absolute h-3.5 w-3.5 rounded-full"
            style={{ top: "-7px", left: "50%", marginLeft: "-7px", background: r.color, boxShadow: `0 0 18px ${r.color}` }}
          />
          <span
            className="absolute h-2.5 w-2.5 rounded-full"
            style={{ bottom: "-5px", left: "50%", marginLeft: "-5px", background: r.color, boxShadow: `0 0 14px ${r.color}` }}
          />
        </motion.div>
      ))}
      <motion.div
        className="absolute inset-0 m-auto grid place-items-center rounded-full brand-gradient animate-gradient"
        style={{ width: "26%", height: "26%" }}
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="h-1/2 w-1/2 rounded-full bg-white/90 blur-[2px]" />
      </motion.div>
    </div>
  );
};

/* ============================================================
   3. DATA FLOW PIPELINE — colorful particles travelling
   ============================================================ */
const FLOW_PATH = "M0 60 C 200 20, 300 100, 500 60 S 800 20, 1000 60";
export const FlowPipeline = ({ className = "" }) => (
  <svg aria-hidden viewBox="0 0 1000 120" className={className} fill="none" preserveAspectRatio="none">
    <defs>
      <linearGradient id="flow-line" x1="0" y1="0" x2="1000" y2="0" gradientUnits="userSpaceOnUse">
        <stop stopColor="#123C52" />
        <stop offset="0.35" stopColor="#0E8F9E" />
        <stop offset="0.7" stopColor="#F5C542" />
        <stop offset="1" stopColor="#29B3C2" />
      </linearGradient>
    </defs>
    <path d={FLOW_PATH} stroke="url(#flow-line)" strokeWidth="2.5" strokeOpacity="0.55" />
    {[0, 1, 2, 3, 4].map((i) => (
      <circle key={i} r="4.5" fill={COLORS[i % COLORS.length]}>
        <animateMotion dur="4s" repeatCount="indefinite" begin={`${i * 0.8}s`} path={FLOW_PATH} />
        <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin={`${i * 0.8}s`} />
      </circle>
    ))}
  </svg>
);

/* ============================================================
   4. AURORA MESH — colorful animated gradient blobs
   ============================================================ */
export const AuroraMesh = ({ className = "" }) => (
  <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
    <motion.div
      className="absolute rounded-full blur-[120px]"
      style={{ width: 520, height: 520, top: "-12%", right: "2%", background: "radial-gradient(circle,rgba(14,143,158,0.55),transparent 70%)" }}
      animate={{ x: [0, -50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute rounded-full blur-[120px]"
      style={{ width: 460, height: 460, top: "10%", left: "8%", background: "radial-gradient(circle,rgba(18,60,82,0.5),transparent 70%)" }}
      animate={{ x: [0, 40, 0], y: [0, 40, 0], scale: [1, 1.12, 1] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute rounded-full blur-[120px]"
      style={{ width: 420, height: 420, bottom: "-16%", left: "32%", background: "radial-gradient(circle,rgba(245,197,66,0.45),transparent 70%)" }}
      animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.15, 1] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute rounded-full blur-[110px]"
      style={{ width: 360, height: 360, bottom: "0%", right: "24%", background: "radial-gradient(circle,rgba(41,179,194,0.4),transparent 70%)" }}
      animate={{ x: [0, -30, 0], y: [0, -20, 0], scale: [1, 1.1, 1] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);
